import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {DragGhostDirection, ResizeEvent, ResizePhase, Resizer} from '@mintdata-wsp/wsp/ui/components/resizable/Resizer';
import {SurfaceComponents} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/components/SurfaceComponents';
import {SurfacePagesOutline} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/pages/SurfacePagesOutline';
import {SurfaceBoxesOutline} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/SurfaceBoxesOutline';
import {SurfaceCopyPagesOutline} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/copyPages/SurfaceCopyPagesOutline';
import {Section} from '@mintdata/mint-kit/impl/properties/components/section/Section';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, computed, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./SurfaceOutlinePanel.less');
const cx = classnames.bind(styles);

const SECTION_COLLAPSED_HEIGHT_PX:number = 32;

const COPY_PAGES_MIN_HEIGHT_PX:number = 142; // with collapsed footer
const COPY_PAGES_DEFAULT_HEIGHT_PX:number = 250; // with collapsed footer

const PAGES_MIN_HEIGHT_PX:number = 58;
const COMPONENTS_MIN_HEIGHT_PX:number = 110;
const BOXES_MIN_HEIGHT_PX:number = 58;

interface SurfaceOutlinePanelProps
{
    className?:string,
}

interface SurfaceOutlinePanelState
{
    copyFromResizerInProgressOffset:number,
    pagesResizerInProgressOffset:number,
    componentsResizerInProgressOffset:number,
}

@observer
export class SurfaceOutlinePanel extends React.Component<SurfaceOutlinePanelProps, SurfaceOutlinePanelState>
{
    private _surfaceOutlinePanelRef:HTMLElement | null;

    @observable
    private _state:SurfaceOutlinePanelState = {
        copyFromResizerInProgressOffset: 0,
        pagesResizerInProgressOffset: 0,
        componentsResizerInProgressOffset: 0,
    };

    constructor(props:any)
    {
        super(props);

        const {workbenchVisibility: wv} = controllers.ui;

        wv.setOnCopyPagesPanelShowCb(this._onCopyPagesPanelShow);
        wv.setOnCopyPagesPanelHideCb(this._onCopyPagesPanelHide);
        wv.setOnCopyPagesPanelExpandedPartHeightChange(this._onCopyPagesPanelExpandPartHeightChange);
    }

    componentWillUnmount():void
    {
        const {workbenchVisibility: wv} = controllers.ui;

        wv.setOnCopyPagesPanelShowCb(null);
        wv.setOnCopyPagesPanelHideCb(null);
        wv.setOnCopyPagesPanelExpandedPartHeightChange(null);
    }

    @bind
    private _refHandlerSurfaceOutline(ref:HTMLElement | null):void
    {
        this._surfaceOutlinePanelRef = ref;
    }

    @computed
    private get _copyPagesWrapperHeight():string
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const copyFromPanelHeight:number = wv.getSurfaceCopyPagesPanelHeight();

        return wv.isSurfaceCopyPagesPanelShown()
            ? `${copyFromPanelHeight}%`
            : '0';
    }

    @computed
    private get _pagesOutlineWrapperHeight():string
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const pagesOutlinePanelHeight:number = wv.getSurfacePagesOutlinePanelHeight();

        return wv.isSurfacePagesOutlinePanelCollapsed()
            ? `${SECTION_COLLAPSED_HEIGHT_PX}px`
            : `${pagesOutlinePanelHeight}%`;
    }

    @computed
    private get _componentsWrapperHeight():string
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const componentsPanelHeight:number = wv.getSurfaceComponentsPanelHeight();

        return wv.isSurfaceComponentsPanelCollapsed()
            ? `${SECTION_COLLAPSED_HEIGHT_PX}px`
            : `${componentsPanelHeight}%`;
    }

    @computed
    private get _boxesOutlineWrapperHeight():string
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const boxesOutlinePanelHeight:number = wv.getSurfaceBoxesOutlinePanelHeight();

        return wv.isSurfaceBoxesOutlinePanelCollapsed()
            ? `${SECTION_COLLAPSED_HEIGHT_PX}px`
            : `${boxesOutlinePanelHeight}%`;
    }

    @computed
    private get _copyPagesPanelHeightInPx():number
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const copyPagesPanelHeight:number = wv.getSurfaceCopyPagesPanelHeight();
        const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

        return wv.isSurfaceCopyPagesPanelShown()
            ? copyPagesPanelHeight * onePercentToPx
            : 0;
    }

    @computed
    private get _copyPagesDefaultHeightPx():number
    {
        const {workbenchVisibility: wv} = controllers.ui;

        return COPY_PAGES_DEFAULT_HEIGHT_PX + wv.getSurfaceCopyPagesPanelExpandedPartHeight();
    }

    @computed
    private get _copyPagesMintHeightPx():number
    {
        const {workbenchVisibility: wv} = controllers.ui;

        return COPY_PAGES_MIN_HEIGHT_PX + wv.getSurfaceCopyPagesPanelExpandedPartHeight();
    }

    @computed
    private get _pagesPanelHeightInPx():number
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const pagesOutlinePanelHeight:number = wv.getSurfacePagesOutlinePanelHeight();
        const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

        return wv.isSurfacePagesOutlinePanelCollapsed()
            ? SECTION_COLLAPSED_HEIGHT_PX
            : pagesOutlinePanelHeight * onePercentToPx;
    }

    @computed
    private get _componentsPanelHeightInPx():number
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const componentsPanelHeight:number = wv.getSurfaceComponentsPanelHeight();
        const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

        return wv.isSurfaceComponentsPanelCollapsed()
            ? SECTION_COLLAPSED_HEIGHT_PX
            : componentsPanelHeight * onePercentToPx;
    }

    @computed
    private get _boxesPanelHeightInPx():number
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const boxesOutlinePanelHeight:number = wv.getSurfaceBoxesOutlinePanelHeight();
        const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

        return wv.isSurfaceBoxesOutlinePanelCollapsed()
            ? SECTION_COLLAPSED_HEIGHT_PX
            : boxesOutlinePanelHeight * onePercentToPx;
    }

    private get _heightOfSurfaceOutlinePanelInPx():number
    {
        if( !this._surfaceOutlinePanelRef )
            return 0;

        return this._surfaceOutlinePanelRef.getBoundingClientRect().height;
    }

    // COPY FROM / PAGES Resizer
    @bind
    private _getCopyFromMaxResizerInProgressOffset():number
    {
        const {copyFromResizerInProgressOffset} = this._state;
        const {workbenchVisibility: wv} = controllers.ui;
        const isSurfacePagesOutlinePanelCollapsed:boolean = wv.isSurfacePagesOutlinePanelCollapsed();
        const isSurfaceComponentsPanelCollapsed:boolean = wv.isSurfaceComponentsPanelCollapsed();
        const isSurfaceBoxesOutlinePanelCollapsed:boolean = wv.isSurfaceBoxesOutlinePanelCollapsed();

        if( !this._surfaceOutlinePanelRef )
            return 0;

        if( copyFromResizerInProgressOffset >= 0 )
        {
            return this._copyPagesPanelHeightInPx - copyFromResizerInProgressOffset > this._copyPagesMintHeightPx
                ? copyFromResizerInProgressOffset
                : this._copyPagesPanelHeightInPx - this._copyPagesMintHeightPx;
        }

        let copyPagesMaxHeightPx:number = 0;

        if( !isSurfacePagesOutlinePanelCollapsed )
            copyPagesMaxHeightPx = this._copyPagesPanelHeightInPx + this._pagesPanelHeightInPx - PAGES_MIN_HEIGHT_PX;
        else if( !isSurfaceComponentsPanelCollapsed )
            copyPagesMaxHeightPx = this._copyPagesPanelHeightInPx + this._componentsPanelHeightInPx - COMPONENTS_MIN_HEIGHT_PX;
        else if( !isSurfaceBoxesOutlinePanelCollapsed )
            copyPagesMaxHeightPx = this._copyPagesPanelHeightInPx + this._boxesPanelHeightInPx - BOXES_MIN_HEIGHT_PX;

        return this._copyPagesPanelHeightInPx - copyFromResizerInProgressOffset < copyPagesMaxHeightPx
            ? copyFromResizerInProgressOffset
            : this._copyPagesPanelHeightInPx - copyPagesMaxHeightPx;
    }

    @action.bound
    private _handleCopyFromOutlineResize(event:ResizeEvent):void
    {
        if( !this._surfaceOutlinePanelRef )
            return;

        this._state.copyFromResizerInProgressOffset -= event.deltaY;

        if( event.phase === ResizePhase.Stop )
        {
            const copyFromMaxResizerInProgressOffset:number = this._getCopyFromMaxResizerInProgressOffset();
            const {workbenchVisibility: wv} = controllers.ui;

            const isSurfacePagesOutlinePanelCollapsed:boolean = wv.isSurfacePagesOutlinePanelCollapsed();
            const isSurfaceComponentsPanelCollapsed:boolean = wv.isSurfaceComponentsPanelCollapsed();
            const isSurfaceBoxesOutlinePanelCollapsed:boolean = wv.isSurfaceBoxesOutlinePanelCollapsed();

            if( copyFromMaxResizerInProgressOffset !== 0 )
            {
                const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

                if( !isSurfacePagesOutlinePanelCollapsed )
                {
                    const copyPagesMaxHeightPx = this._copyPagesPanelHeightInPx + this._pagesPanelHeightInPx - PAGES_MIN_HEIGHT_PX;
                    this._setPanelsSizesAfterCopyPagesResize(
                        copyPagesMaxHeightPx,
                        onePercentToPx,
                        this._pagesPanelHeightInPx,
                        PAGES_MIN_HEIGHT_PX,
                        wv.setSurfacePagesOutlinePanelHeight
                    );
                }
                else if( !isSurfaceComponentsPanelCollapsed )
                {
                    const copyPagesMaxHeightPx = this._copyPagesPanelHeightInPx + this._componentsPanelHeightInPx - COMPONENTS_MIN_HEIGHT_PX;
                    this._setPanelsSizesAfterCopyPagesResize(
                        copyPagesMaxHeightPx,
                        onePercentToPx,
                        this._componentsPanelHeightInPx,
                        COMPONENTS_MIN_HEIGHT_PX,
                        wv.setSurfaceComponentsPanelHeight
                    );
                }
                else if( !isSurfaceBoxesOutlinePanelCollapsed )
                {
                    const copyPagesMaxHeightPx = this._copyPagesPanelHeightInPx + this._boxesPanelHeightInPx - BOXES_MIN_HEIGHT_PX;
                    this._setPanelsSizesAfterCopyPagesResize(
                        copyPagesMaxHeightPx,
                        onePercentToPx,
                        this._boxesPanelHeightInPx,
                        BOXES_MIN_HEIGHT_PX,
                        wv.setSurfaceBoxesOutlinePanelHeight
                    );
                }
            }

            this._state.copyFromResizerInProgressOffset = 0;
        }
    }

    @bind
    private _setPanelsSizesAfterCopyPagesResize(copyPagesMaxHeightPx:number,
                                                onePercentToPx:number,
                                                panelHeightInPx:number,
                                                panelMinHeight:number,
                                                setterForPanel:(height:number) => void)
    {
        const copyFromMaxResizerInProgressOffset:number = this._getCopyFromMaxResizerInProgressOffset();
        const newHeightInPx:number = this._copyPagesPanelHeightInPx - copyFromMaxResizerInProgressOffset;
        const {workbenchVisibility: wv} = controllers.ui;

        if( newHeightInPx === this._copyPagesMintHeightPx )
        {
            setterForPanel(
                (panelHeightInPx + this._copyPagesPanelHeightInPx - this._copyPagesMintHeightPx)
                / onePercentToPx
            );
            wv.setSurfaceCopyPagesPanelHeight(this._copyPagesMintHeightPx / onePercentToPx);
        }
        else if( newHeightInPx > copyPagesMaxHeightPx )
        {
            setterForPanel(panelMinHeight / onePercentToPx);
            wv.setSurfaceCopyPagesPanelHeight(copyPagesMaxHeightPx / onePercentToPx);
        }
        else
        {
            setterForPanel((panelHeightInPx + copyFromMaxResizerInProgressOffset) / onePercentToPx);
            wv.setSurfaceCopyPagesPanelHeight(newHeightInPx / onePercentToPx);
        }
    }

    // PAGES OUTLINE Resizer
    @bind
    private _getPagesOutlineMaxResizerInProgressOffset():number
    {
        const {pagesResizerInProgressOffset} = this._state;
        const isSurfaceComponentsPanelCollapsed:boolean = controllers.ui.workbenchVisibility.isSurfaceComponentsPanelCollapsed();

        if( !this._surfaceOutlinePanelRef )
            return 0;

        const PAGES_MAX_HEIGHT_PX:number = !isSurfaceComponentsPanelCollapsed
            ? this._pagesPanelHeightInPx + this._componentsPanelHeightInPx - COMPONENTS_MIN_HEIGHT_PX
            : this._pagesPanelHeightInPx + this._boxesPanelHeightInPx - BOXES_MIN_HEIGHT_PX;

        if( pagesResizerInProgressOffset >= 0 )
        {
            return this._pagesPanelHeightInPx - pagesResizerInProgressOffset > SECTION_COLLAPSED_HEIGHT_PX
                ? pagesResizerInProgressOffset
                : this._pagesPanelHeightInPx - SECTION_COLLAPSED_HEIGHT_PX;
        }

        return this._pagesPanelHeightInPx - pagesResizerInProgressOffset < PAGES_MAX_HEIGHT_PX
            ? pagesResizerInProgressOffset
            : this._pagesPanelHeightInPx - PAGES_MAX_HEIGHT_PX;
    }

    @action.bound
    private _handlePagesOutlineResize(event:ResizeEvent):void
    {
        if( !this._surfaceOutlinePanelRef )
            return;

        this._state.pagesResizerInProgressOffset -= event.deltaY;

        if( event.phase === ResizePhase.Stop )
        {
            const pagesOutlineMaxResizerInProgressOffset:number = this._getPagesOutlineMaxResizerInProgressOffset();
            const {workbenchVisibility: wv} = controllers.ui;
            const isSurfaceComponentsPanelCollapsed:boolean = wv.isSurfaceComponentsPanelCollapsed();

            const PAGES_MAX_HEIGHT_PX:number = !isSurfaceComponentsPanelCollapsed
                ? this._pagesPanelHeightInPx + this._componentsPanelHeightInPx - COMPONENTS_MIN_HEIGHT_PX
                : this._pagesPanelHeightInPx + this._boxesPanelHeightInPx - BOXES_MIN_HEIGHT_PX;

            if( pagesOutlineMaxResizerInProgressOffset !== 0 )
            {
                const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;
                const newHeightInPx:number = this._pagesPanelHeightInPx - pagesOutlineMaxResizerInProgressOffset;

                if( newHeightInPx < PAGES_MIN_HEIGHT_PX )
                {
                    wv.setSurfacePagesOutlinePanelCollapsed(true);
                }
                else if( newHeightInPx > PAGES_MAX_HEIGHT_PX )
                {
                    wv.setSurfacePagesOutlinePanelHeight(PAGES_MAX_HEIGHT_PX / onePercentToPx);

                    if( !isSurfaceComponentsPanelCollapsed )
                        wv.setSurfaceComponentsPanelHeight(COMPONENTS_MIN_HEIGHT_PX / onePercentToPx);
                    else
                        wv.setSurfaceBoxesOutlinePanelHeight(BOXES_MIN_HEIGHT_PX / onePercentToPx);
                }
                else
                {
                    wv.setSurfacePagesOutlinePanelHeight(newHeightInPx / onePercentToPx);

                    if( !isSurfaceComponentsPanelCollapsed )
                        wv.setSurfaceComponentsPanelHeight(
                            (this._componentsPanelHeightInPx + pagesOutlineMaxResizerInProgressOffset) / onePercentToPx
                        );
                    else
                    {
                        wv.setSurfaceBoxesOutlinePanelHeight(
                            (this._boxesPanelHeightInPx + pagesOutlineMaxResizerInProgressOffset) / onePercentToPx
                        );
                    }
                }
            }

            this._state.pagesResizerInProgressOffset = 0;
        }
    }

    // COMPONENTS Resizer
    @bind
    private _getComponentsMaxResizerInProgressOffset():number
    {
        const {componentsResizerInProgressOffset} = this._state;

        if( !this._surfaceOutlinePanelRef )
            return 0;

        const COMPONENTS_MAX_HEIGHT_PX:number = this._heightOfSurfaceOutlinePanelInPx - this._pagesPanelHeightInPx - BOXES_MIN_HEIGHT_PX - this._copyPagesPanelHeightInPx;

        if( componentsResizerInProgressOffset >= 0 )
        {
            return this._componentsPanelHeightInPx - componentsResizerInProgressOffset > SECTION_COLLAPSED_HEIGHT_PX
                ? componentsResizerInProgressOffset
                : this._componentsPanelHeightInPx - SECTION_COLLAPSED_HEIGHT_PX;
        }

        return this._componentsPanelHeightInPx - componentsResizerInProgressOffset < COMPONENTS_MAX_HEIGHT_PX
            ? componentsResizerInProgressOffset
            : this._componentsPanelHeightInPx - COMPONENTS_MAX_HEIGHT_PX;
    }

    @action.bound
    private _handleComponentsResize(event:ResizeEvent):void
    {
        if( !this._surfaceOutlinePanelRef )
            return;

        this._state.componentsResizerInProgressOffset -= event.deltaY;

        if( event.phase === ResizePhase.Stop )
        {
            const {workbenchVisibility: wv} = controllers.ui;
            const COMPONENTS_MAX_HEIGHT_PX:number = this._heightOfSurfaceOutlinePanelInPx - this._pagesPanelHeightInPx - BOXES_MIN_HEIGHT_PX - this._copyPagesPanelHeightInPx;

            const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;
            const componentsMaxResizerInProgressOffset:number = this._getComponentsMaxResizerInProgressOffset();
            const componentsAndBoxesHeight:number = this._componentsPanelHeightInPx + this._boxesPanelHeightInPx;
            const newHeightInPx:number = this._componentsPanelHeightInPx - componentsMaxResizerInProgressOffset;

            if( newHeightInPx < COMPONENTS_MIN_HEIGHT_PX )
            {
                wv.setSurfaceComponentsPanelCollapsed(true);
            }
            else if( newHeightInPx > COMPONENTS_MAX_HEIGHT_PX )
            {
                wv.setSurfaceComponentsPanelHeight(COMPONENTS_MAX_HEIGHT_PX / onePercentToPx);
                wv.setSurfaceBoxesOutlinePanelHeight(BOXES_MIN_HEIGHT_PX / onePercentToPx);
            }
            else
            {
                wv.setSurfaceComponentsPanelHeight(newHeightInPx / onePercentToPx);
                wv.setSurfaceBoxesOutlinePanelHeight((componentsAndBoxesHeight - newHeightInPx) / onePercentToPx);
            }

            this._state.componentsResizerInProgressOffset = 0;
        }
    }

    // PAGES OUTLINE Collapse/Expand
    @action.bound
    private _handlePagesOutlinePanelCollapse():void
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const isSurfaceComponentsPanelCollapsed:boolean = wv.isSurfaceComponentsPanelCollapsed();
        const isSurfaceBoxesOutlinePanelCollapsed:boolean = wv.isSurfaceBoxesOutlinePanelCollapsed();
        const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

        if( isSurfaceComponentsPanelCollapsed && isSurfaceBoxesOutlinePanelCollapsed )
        {
            wv.setSurfaceCopyPagesPanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - 3 * SECTION_COLLAPSED_HEIGHT_PX) / onePercentToPx
            );
            return;
        }

        if( !isSurfaceComponentsPanelCollapsed )
            wv.setSurfaceComponentsPanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - this._boxesPanelHeightInPx - this._copyPagesPanelHeightInPx)
                / onePercentToPx
            );
        else
            wv.setSurfaceBoxesOutlinePanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - this._componentsPanelHeightInPx - this._copyPagesPanelHeightInPx)
                / onePercentToPx
            );
    }

    @action.bound
    private _handlePagesOutlinePanelExpand():void
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const isSurfaceCopyPagesPanelShown:boolean = wv.isSurfaceCopyPagesPanelShown();
        const isSurfaceComponentsPanelCollapsed:boolean = wv.isSurfaceComponentsPanelCollapsed();
        const isSurfaceBoxesOutlinePanelCollapsed:boolean = wv.isSurfaceBoxesOutlinePanelCollapsed();
        const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

        if( isSurfaceComponentsPanelCollapsed && isSurfaceBoxesOutlinePanelCollapsed )
        {
            const freeSpacePx:number = this._heightOfSurfaceOutlinePanelInPx - 2 * SECTION_COLLAPSED_HEIGHT_PX;

            if( !isSurfaceCopyPagesPanelShown )
                wv.setSurfacePagesOutlinePanelHeight(freeSpacePx / onePercentToPx);
            else
            {
                if( freeSpacePx - this._pagesPanelHeightInPx >= this._copyPagesMintHeightPx )
                {
                    wv.setSurfaceCopyPagesPanelHeight((freeSpacePx - this._pagesPanelHeightInPx) / onePercentToPx);
                }
                else
                {
                    wv.setSurfaceCopyPagesPanelHeight((this._copyPagesMintHeightPx) / onePercentToPx);
                    wv.setSurfacePagesOutlinePanelHeight((freeSpacePx - this._copyPagesMintHeightPx) / onePercentToPx);
                }
            }
        }
        else if( !isSurfaceComponentsPanelCollapsed )
        {
            const pagesCollapsedAndComponentsHeightInPx:number = SECTION_COLLAPSED_HEIGHT_PX + this._componentsPanelHeightInPx;

            if( pagesCollapsedAndComponentsHeightInPx - this._pagesPanelHeightInPx >= COMPONENTS_MIN_HEIGHT_PX )
                wv.setSurfaceComponentsPanelHeight(
                    (pagesCollapsedAndComponentsHeightInPx - this._pagesPanelHeightInPx) / onePercentToPx
                );
            else
            {
                wv.setSurfaceComponentsPanelHeight(COMPONENTS_MIN_HEIGHT_PX / onePercentToPx);

                if( pagesCollapsedAndComponentsHeightInPx - PAGES_MIN_HEIGHT_PX - COMPONENTS_MIN_HEIGHT_PX >= 0 )
                {
                    wv.setSurfacePagesOutlinePanelHeight(
                        (pagesCollapsedAndComponentsHeightInPx - COMPONENTS_MIN_HEIGHT_PX) / onePercentToPx
                    );
                }
                else
                {
                    wv.setSurfacePagesOutlinePanelHeight(PAGES_MIN_HEIGHT_PX / onePercentToPx);

                    if( !isSurfaceBoxesOutlinePanelCollapsed )
                    {
                        const spaceForBoxesAndCopyPagesPx:number = this._heightOfSurfaceOutlinePanelInPx - COMPONENTS_MIN_HEIGHT_PX - PAGES_MIN_HEIGHT_PX;

                        if (!isSurfaceCopyPagesPanelShown)
                            wv.setSurfaceBoxesOutlinePanelHeight(spaceForBoxesAndCopyPagesPx / onePercentToPx);
                        else
                        {
                            if (spaceForBoxesAndCopyPagesPx - this._copyPagesPanelHeightInPx > BOXES_MIN_HEIGHT_PX)
                                wv.setSurfaceBoxesOutlinePanelHeight(
                                    (spaceForBoxesAndCopyPagesPx - this._copyPagesPanelHeightInPx) / onePercentToPx
                                );
                            else
                            {
                                wv.setSurfaceBoxesOutlinePanelHeight(BOXES_MIN_HEIGHT_PX / onePercentToPx);
                                wv.setSurfaceCopyPagesPanelHeight(spaceForBoxesAndCopyPagesPx - BOXES_MIN_HEIGHT_PX)
                            }
                        }
                    }
                    else if( isSurfaceCopyPagesPanelShown )
                    {
                        wv.setSurfaceCopyPagesPanelHeight(
                            (this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - COMPONENTS_MIN_HEIGHT_PX - PAGES_MIN_HEIGHT_PX)
                            / onePercentToPx
                        );
                    }
                }
            }
        }
        else
        {
            const allHeightForPagesAndBoxesInPx:number = this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - this._copyPagesPanelHeightInPx;

            if( allHeightForPagesAndBoxesInPx - this._pagesPanelHeightInPx >= BOXES_MIN_HEIGHT_PX )
                wv.setSurfaceBoxesOutlinePanelHeight(
                    (allHeightForPagesAndBoxesInPx - this._pagesPanelHeightInPx) / onePercentToPx
                );
            else
            {
                wv.setSurfaceBoxesOutlinePanelHeight(BOXES_MIN_HEIGHT_PX / onePercentToPx);

                if (!isSurfaceCopyPagesPanelShown)
                    wv.setSurfacePagesOutlinePanelHeight(
                        (allHeightForPagesAndBoxesInPx - BOXES_MIN_HEIGHT_PX) / onePercentToPx
                    );
                else
                {
                    const spaceForPagesAndCopyPagesPx:number = this._heightOfSurfaceOutlinePanelInPx - BOXES_MIN_HEIGHT_PX - SECTION_COLLAPSED_HEIGHT_PX;

                    if (spaceForPagesAndCopyPagesPx - this._copyPagesPanelHeightInPx > PAGES_MIN_HEIGHT_PX)
                        wv.setSurfacePagesOutlinePanelHeight(
                            (spaceForPagesAndCopyPagesPx - this._copyPagesPanelHeightInPx) / onePercentToPx
                        );
                    else
                    {
                        wv.setSurfacePagesOutlinePanelHeight(PAGES_MIN_HEIGHT_PX / onePercentToPx);
                        wv.setSurfaceCopyPagesPanelHeight((spaceForPagesAndCopyPagesPx - PAGES_MIN_HEIGHT_PX) / onePercentToPx);
                    }
                }
            }
        }
    }

    // COMPONENTS Collapse/Expand
    @action.bound
    private _handleComponentsPanelCollapse():void
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const isSurfacePagesOutlinePanelCollapsed:boolean = wv.isSurfacePagesOutlinePanelCollapsed();
        const isSurfaceBoxesOutlinePanelCollapsed:boolean = wv.isSurfaceBoxesOutlinePanelCollapsed();
        const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

        if( isSurfacePagesOutlinePanelCollapsed && isSurfaceBoxesOutlinePanelCollapsed )
        {
            wv.setSurfaceCopyPagesPanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - 3 * SECTION_COLLAPSED_HEIGHT_PX) / onePercentToPx
            );
            return;
        }

        if( !isSurfaceBoxesOutlinePanelCollapsed )
            wv.setSurfaceBoxesOutlinePanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - this._pagesPanelHeightInPx - this._copyPagesPanelHeightInPx)
                / onePercentToPx
            );
        else
            wv.setSurfacePagesOutlinePanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - this._boxesPanelHeightInPx - this._copyPagesPanelHeightInPx)
                / onePercentToPx
            );
    }

    @action.bound
    private _handleComponentsPanelExpand():void
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const isSurfaceCopyPagesPanelShown:boolean = wv.isSurfaceCopyPagesPanelShown();
        const isSurfacePagesOutlinePanelCollapsed:boolean = wv.isSurfacePagesOutlinePanelCollapsed();
        const isSurfaceBoxesOutlinePanelCollapsed:boolean = wv.isSurfaceBoxesOutlinePanelCollapsed();
        const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

        if( isSurfacePagesOutlinePanelCollapsed && isSurfaceBoxesOutlinePanelCollapsed )
        {
            const freeSpacePx:number = this._heightOfSurfaceOutlinePanelInPx - 2 * SECTION_COLLAPSED_HEIGHT_PX;

            if( !isSurfaceCopyPagesPanelShown )
                wv.setSurfaceComponentsPanelHeight(freeSpacePx / onePercentToPx);
            else
            {
                if( freeSpacePx - this._componentsPanelHeightInPx >= this._copyPagesMintHeightPx )
                {
                    wv.setSurfaceCopyPagesPanelHeight((freeSpacePx - this._componentsPanelHeightInPx) / onePercentToPx);
                }
                else
                {
                    wv.setSurfaceCopyPagesPanelHeight((this._copyPagesMintHeightPx) / onePercentToPx);
                    wv.setSurfaceComponentsPanelHeight((freeSpacePx - this._copyPagesMintHeightPx) / onePercentToPx);
                }
            }
        }
        else if( !isSurfaceBoxesOutlinePanelCollapsed )
        {
            const componentsCollapsedAndBoxesHeightInPx:number = SECTION_COLLAPSED_HEIGHT_PX + this._boxesPanelHeightInPx;

            if( componentsCollapsedAndBoxesHeightInPx - this._componentsPanelHeightInPx > BOXES_MIN_HEIGHT_PX )
                wv.setSurfaceBoxesOutlinePanelHeight(
                    (componentsCollapsedAndBoxesHeightInPx - this._componentsPanelHeightInPx) / onePercentToPx
                );
            else
            {
                wv.setSurfaceBoxesOutlinePanelHeight(BOXES_MIN_HEIGHT_PX / onePercentToPx);

                if( componentsCollapsedAndBoxesHeightInPx - COMPONENTS_MIN_HEIGHT_PX - BOXES_MIN_HEIGHT_PX >= 0 )
                {
                    wv.setSurfaceComponentsPanelHeight(
                        (componentsCollapsedAndBoxesHeightInPx - BOXES_MIN_HEIGHT_PX) / onePercentToPx
                    );
                }
                else
                {
                    wv.setSurfaceComponentsPanelHeight(COMPONENTS_MIN_HEIGHT_PX / onePercentToPx);

                    if( !isSurfacePagesOutlinePanelCollapsed )
                    {
                        const spaceForPagesAndCopyPagesPx:number = this._heightOfSurfaceOutlinePanelInPx - BOXES_MIN_HEIGHT_PX - COMPONENTS_MIN_HEIGHT_PX;

                        if (!isSurfaceCopyPagesPanelShown)
                            wv.setSurfacePagesOutlinePanelHeight(spaceForPagesAndCopyPagesPx/ onePercentToPx);
                        else
                        {
                            if (spaceForPagesAndCopyPagesPx - this._copyPagesPanelHeightInPx > PAGES_MIN_HEIGHT_PX)
                                wv.setSurfacePagesOutlinePanelHeight(
                                    (spaceForPagesAndCopyPagesPx - this._copyPagesPanelHeightInPx) / onePercentToPx
                                );
                            else
                            {
                                wv.setSurfacePagesOutlinePanelHeight(PAGES_MIN_HEIGHT_PX / onePercentToPx);
                                wv.setSurfaceCopyPagesPanelHeight(spaceForPagesAndCopyPagesPx - PAGES_MIN_HEIGHT_PX)
                            }
                        }
                    }
                    else if( isSurfaceCopyPagesPanelShown )
                    {
                        wv.setSurfaceCopyPagesPanelHeight(
                            (this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - BOXES_MIN_HEIGHT_PX - COMPONENTS_MIN_HEIGHT_PX)
                            / onePercentToPx
                        );
                    }
                }
            }
        }
        else
        {
            const allHeightForPagesAndComponentsInPx:number = this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - this._copyPagesPanelHeightInPx;

            if( allHeightForPagesAndComponentsInPx - this._componentsPanelHeightInPx >= PAGES_MIN_HEIGHT_PX )
                wv.setSurfacePagesOutlinePanelHeight(
                    (allHeightForPagesAndComponentsInPx - this._componentsPanelHeightInPx) / onePercentToPx
                );
            else
            {
                wv.setSurfacePagesOutlinePanelHeight(PAGES_MIN_HEIGHT_PX / onePercentToPx);

                if (!isSurfaceCopyPagesPanelShown)
                    wv.setSurfaceComponentsPanelHeight(
                        (allHeightForPagesAndComponentsInPx - PAGES_MIN_HEIGHT_PX) / onePercentToPx
                    );
                else
                {
                    const spaceForComponentsAndCopyPagesPx:number = this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - PAGES_MIN_HEIGHT_PX;

                    if (spaceForComponentsAndCopyPagesPx - this._copyPagesPanelHeightInPx > COMPONENTS_MIN_HEIGHT_PX)
                        wv.setSurfaceComponentsPanelHeight(
                            (spaceForComponentsAndCopyPagesPx - this._copyPagesPanelHeightInPx) / onePercentToPx
                        );
                    else
                    {
                        wv.setSurfaceComponentsPanelHeight(COMPONENTS_MIN_HEIGHT_PX / onePercentToPx);
                        wv.setSurfaceCopyPagesPanelHeight((spaceForComponentsAndCopyPagesPx - COMPONENTS_MIN_HEIGHT_PX) / onePercentToPx);
                    }
                }
            }
        }
    }

    // BOXES OUTLINE Collapse/Expand
    @action.bound
    private _handleBoxesOutlinePanelCollapse():void
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const isSurfacePagesOutlinePanelCollapsed:boolean = wv.isSurfacePagesOutlinePanelCollapsed();
        const isSurfaceComponentsPanelCollapsed:boolean = wv.isSurfaceComponentsPanelCollapsed();
        const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

        if( isSurfacePagesOutlinePanelCollapsed && isSurfaceComponentsPanelCollapsed )
        {
            wv.setSurfaceCopyPagesPanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - 3 * SECTION_COLLAPSED_HEIGHT_PX) / onePercentToPx
            );
            return;
        }

        if( !isSurfaceComponentsPanelCollapsed )
            wv.setSurfaceComponentsPanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - this._pagesPanelHeightInPx - this._copyPagesPanelHeightInPx)
                / onePercentToPx
            );
        else
            wv.setSurfacePagesOutlinePanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - this._componentsPanelHeightInPx - this._copyPagesPanelHeightInPx)
                / onePercentToPx
            );
    }

    @action.bound
    private _handleBoxesOutlinePanelExpand():void
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const isSurfaceCopyPagesPanelShown:boolean = wv.isSurfaceCopyPagesPanelShown();
        const isSurfacePagesOutlinePanelCollapsed:boolean = wv.isSurfacePagesOutlinePanelCollapsed();
        const isSurfaceComponentsPanelCollapsed:boolean = wv.isSurfaceComponentsPanelCollapsed();

        const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

        if( isSurfacePagesOutlinePanelCollapsed && isSurfaceComponentsPanelCollapsed )
        {
            const freeSpacePx:number = this._heightOfSurfaceOutlinePanelInPx - 2 * SECTION_COLLAPSED_HEIGHT_PX;

            if( !isSurfaceCopyPagesPanelShown )
                wv.setSurfaceBoxesOutlinePanelHeight(freeSpacePx / onePercentToPx);
            else
            {
                if( freeSpacePx - this._boxesPanelHeightInPx >= this._copyPagesMintHeightPx )
                {
                    wv.setSurfaceCopyPagesPanelHeight((freeSpacePx - this._boxesPanelHeightInPx) / onePercentToPx);
                }
                else
                {
                    wv.setSurfaceCopyPagesPanelHeight((this._copyPagesMintHeightPx) / onePercentToPx);
                    wv.setSurfaceBoxesOutlinePanelHeight((freeSpacePx - this._copyPagesMintHeightPx) / onePercentToPx);
                }
            }
        }
        else if( !isSurfaceComponentsPanelCollapsed )
        {
            const boxesCollapsedAndComponentsHeightInPx:number = this._componentsPanelHeightInPx + SECTION_COLLAPSED_HEIGHT_PX;

            if( boxesCollapsedAndComponentsHeightInPx - this._boxesPanelHeightInPx > COMPONENTS_MIN_HEIGHT_PX )
                wv.setSurfaceComponentsPanelHeight(
                    (boxesCollapsedAndComponentsHeightInPx - this._boxesPanelHeightInPx) / onePercentToPx
                );
            else
            {
                wv.setSurfaceComponentsPanelHeight(COMPONENTS_MIN_HEIGHT_PX / onePercentToPx);

                if( boxesCollapsedAndComponentsHeightInPx - COMPONENTS_MIN_HEIGHT_PX - BOXES_MIN_HEIGHT_PX >= 0 )
                {
                    wv.setSurfaceBoxesOutlinePanelHeight((boxesCollapsedAndComponentsHeightInPx - COMPONENTS_MIN_HEIGHT_PX) / onePercentToPx);
                }
                else
                {
                    wv.setSurfaceBoxesOutlinePanelHeight(BOXES_MIN_HEIGHT_PX / onePercentToPx);

                    if( !isSurfacePagesOutlinePanelCollapsed )
                    {
                        const spaceForPagesAndCopyPagesPx:number = this._heightOfSurfaceOutlinePanelInPx - BOXES_MIN_HEIGHT_PX - COMPONENTS_MIN_HEIGHT_PX;

                        if (!isSurfaceCopyPagesPanelShown)
                            wv.setSurfacePagesOutlinePanelHeight(spaceForPagesAndCopyPagesPx/ onePercentToPx);
                        else
                        {
                            if (spaceForPagesAndCopyPagesPx - this._copyPagesPanelHeightInPx > PAGES_MIN_HEIGHT_PX)
                                wv.setSurfacePagesOutlinePanelHeight(
                                    (spaceForPagesAndCopyPagesPx - this._copyPagesPanelHeightInPx) / onePercentToPx
                                );
                            else
                            {
                                wv.setSurfacePagesOutlinePanelHeight(PAGES_MIN_HEIGHT_PX / onePercentToPx);
                                wv.setSurfaceCopyPagesPanelHeight(spaceForPagesAndCopyPagesPx - PAGES_MIN_HEIGHT_PX)
                            }
                        }
                    }
                    else if( isSurfaceCopyPagesPanelShown )
                    {
                        wv.setSurfaceCopyPagesPanelHeight(
                            (this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - BOXES_MIN_HEIGHT_PX - COMPONENTS_MIN_HEIGHT_PX)
                            / onePercentToPx
                        );
                    }
                }
            }
        }
        else
        {
            const allHeightForPagesAndBoxesInPx:number = this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - -this._copyPagesPanelHeightInPx;

            if( allHeightForPagesAndBoxesInPx - this._boxesPanelHeightInPx >= PAGES_MIN_HEIGHT_PX )
                wv.setSurfacePagesOutlinePanelHeight(
                    (allHeightForPagesAndBoxesInPx - this._boxesPanelHeightInPx) / onePercentToPx
                );
            else
            {
                wv.setSurfacePagesOutlinePanelHeight(PAGES_MIN_HEIGHT_PX / onePercentToPx);

                if (!isSurfaceCopyPagesPanelShown)
                    wv.setSurfaceBoxesOutlinePanelHeight(
                        (allHeightForPagesAndBoxesInPx - PAGES_MIN_HEIGHT_PX) / onePercentToPx
                    );
                else
                {
                    const spaceForBoxesAndCopyPagesPx:number = this._heightOfSurfaceOutlinePanelInPx - SECTION_COLLAPSED_HEIGHT_PX - PAGES_MIN_HEIGHT_PX;

                    if (spaceForBoxesAndCopyPagesPx - this._copyPagesPanelHeightInPx > BOXES_MIN_HEIGHT_PX)
                        wv.setSurfaceBoxesOutlinePanelHeight(
                            (spaceForBoxesAndCopyPagesPx - this._copyPagesPanelHeightInPx) / onePercentToPx
                        );
                    else
                    {
                        wv.setSurfaceBoxesOutlinePanelHeight(BOXES_MIN_HEIGHT_PX / onePercentToPx);
                        wv.setSurfaceCopyPagesPanelHeight((spaceForBoxesAndCopyPagesPx - BOXES_MIN_HEIGHT_PX) / onePercentToPx);
                    }
                }
            }
        }
    }

    @bind
    private _onCopyPagesPanelShow():void
    {
        this._findPlaceForCopyPagesPanel(this._copyPagesDefaultHeightPx);
    }

    @bind
    private _onCopyPagesPanelExpandPartHeightChange(deltaPx:number):void
    {
        // requestAnimationFrame(() => {
            this._findPlaceForCopyPagesPanel(deltaPx);
        // });
    }

    @action.bound
    private _findPlaceForCopyPagesPanel(deltaPx:number):void
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const isSurfacePagesOutlinePanelCollapsed:boolean = wv.isSurfacePagesOutlinePanelCollapsed();
        const isSurfaceComponentsPanelCollapsed:boolean = wv.isSurfaceComponentsPanelCollapsed();
        const isSurfaceBoxesOutlinePanelCollapsed:boolean = wv.isSurfaceBoxesOutlinePanelCollapsed();

        const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

        wv.setSurfaceCopyPagesPanelHeight((this._copyPagesPanelHeightInPx + deltaPx) / onePercentToPx);

        if( !isSurfaceBoxesOutlinePanelCollapsed )
        {
            if( this._boxesPanelHeightInPx - deltaPx > BOXES_MIN_HEIGHT_PX )
            {
                wv.setSurfaceBoxesOutlinePanelHeight((this._boxesPanelHeightInPx - deltaPx) / onePercentToPx);
            }
            else
            {
                let availableSpace:number = this._boxesPanelHeightInPx - BOXES_MIN_HEIGHT_PX;
                wv.setSurfaceBoxesOutlinePanelHeight(BOXES_MIN_HEIGHT_PX / onePercentToPx);

                if( !isSurfaceComponentsPanelCollapsed )
                {
                    if( this._componentsPanelHeightInPx - COMPONENTS_MIN_HEIGHT_PX > deltaPx - availableSpace )
                    {
                        wv.setSurfaceComponentsPanelHeight((this._componentsPanelHeightInPx - (deltaPx - availableSpace)) / onePercentToPx);
                    }
                    else
                    {
                        availableSpace += this._componentsPanelHeightInPx - COMPONENTS_MIN_HEIGHT_PX;
                        wv.setSurfaceComponentsPanelHeight(COMPONENTS_MIN_HEIGHT_PX / onePercentToPx);

                        if( !isSurfacePagesOutlinePanelCollapsed )
                        {
                            if( this._pagesPanelHeightInPx - PAGES_MIN_HEIGHT_PX > deltaPx - availableSpace )
                            {
                                wv.setSurfacePagesOutlinePanelHeight((this._pagesPanelHeightInPx - (deltaPx - availableSpace)) / onePercentToPx);
                            }
                            else
                            {
                                availableSpace += this._pagesPanelHeightInPx - PAGES_MIN_HEIGHT_PX;
                                wv.setSurfacePagesOutlinePanelHeight(PAGES_MIN_HEIGHT_PX / onePercentToPx);

                                if( this._copyPagesPanelHeightInPx - this._copyPagesMintHeightPx > deltaPx - availableSpace )
                                {
                                    wv.setSurfaceCopyPagesPanelHeight((this._copyPagesPanelHeightInPx - (deltaPx - availableSpace)) / onePercentToPx);
                                }
                                else
                                {
                                    // possibly will be broken layout, need to collapse some opened sections
                                    wv.setSurfaceCopyPagesPanelHeight(this._copyPagesMintHeightPx / onePercentToPx);
                                }
                            }
                        }
                    }
                }
                else if( !isSurfacePagesOutlinePanelCollapsed )
                {
                    if( this._pagesPanelHeightInPx - PAGES_MIN_HEIGHT_PX > deltaPx - availableSpace )
                    {
                        wv.setSurfacePagesOutlinePanelHeight((this._pagesPanelHeightInPx - (deltaPx - availableSpace)) / onePercentToPx);
                    }
                    else
                    {
                        availableSpace += this._pagesPanelHeightInPx - PAGES_MIN_HEIGHT_PX;
                        wv.setSurfacePagesOutlinePanelHeight(PAGES_MIN_HEIGHT_PX / onePercentToPx);

                        if( this._copyPagesPanelHeightInPx - this._copyPagesMintHeightPx > deltaPx - availableSpace )
                        {
                            wv.setSurfaceCopyPagesPanelHeight((this._copyPagesPanelHeightInPx - (deltaPx - availableSpace)) / onePercentToPx);
                        }
                        else
                        {
                            // possibly will be broken layout, need to collapse some opened sections
                            wv.setSurfaceCopyPagesPanelHeight(this._copyPagesMintHeightPx / onePercentToPx);
                        }
                    }
                }
                else
                {
                    // possibly will be broken layout, need to collapse some opened sections
                    wv.setSurfaceCopyPagesPanelHeight(this._copyPagesMintHeightPx / onePercentToPx);
                }
            }
        }
        else if( !isSurfaceComponentsPanelCollapsed )
        {
            if( this._componentsPanelHeightInPx - deltaPx > COMPONENTS_MIN_HEIGHT_PX )
            {
                wv.setSurfaceComponentsPanelHeight((this._componentsPanelHeightInPx - deltaPx) / onePercentToPx);
            }
            else
            {
                let availableSpace:number = this._componentsPanelHeightInPx - COMPONENTS_MIN_HEIGHT_PX;
                wv.setSurfaceComponentsPanelHeight(COMPONENTS_MIN_HEIGHT_PX / onePercentToPx);

                if( !isSurfacePagesOutlinePanelCollapsed )
                {
                    if( this._pagesPanelHeightInPx - PAGES_MIN_HEIGHT_PX > deltaPx - availableSpace )
                    {
                        wv.setSurfacePagesOutlinePanelHeight((this._pagesPanelHeightInPx - (deltaPx - availableSpace)) / onePercentToPx);
                    }
                    else
                    {
                        availableSpace += this._pagesPanelHeightInPx - PAGES_MIN_HEIGHT_PX;
                        wv.setSurfacePagesOutlinePanelHeight(PAGES_MIN_HEIGHT_PX / onePercentToPx);

                        if( this._copyPagesPanelHeightInPx - this._copyPagesMintHeightPx > deltaPx - availableSpace )
                        {
                            wv.setSurfaceCopyPagesPanelHeight((this._copyPagesPanelHeightInPx - (deltaPx - availableSpace)) / onePercentToPx);
                        }
                        else
                        {
                            // possibly will be broken layout, need to collapse some opened sections
                            wv.setSurfaceCopyPagesPanelHeight(this._copyPagesMintHeightPx / onePercentToPx);
                        }
                    }
                }
                else
                {
                    // possibly will be broken layout, need to collapse some opened sections
                    wv.setSurfaceCopyPagesPanelHeight(this._copyPagesMintHeightPx / onePercentToPx);
                }
            }
        }
        else if( !isSurfacePagesOutlinePanelCollapsed )
        {
            if( this._pagesPanelHeightInPx - PAGES_MIN_HEIGHT_PX > deltaPx )
            {
                wv.setSurfacePagesOutlinePanelHeight((this._pagesPanelHeightInPx - deltaPx) / onePercentToPx);
            }
            else
            {
                let availableSpace:number = this._pagesPanelHeightInPx - PAGES_MIN_HEIGHT_PX;
                wv.setSurfacePagesOutlinePanelHeight(PAGES_MIN_HEIGHT_PX / onePercentToPx);

                if( this._copyPagesPanelHeightInPx - this._copyPagesMintHeightPx > deltaPx - availableSpace )
                {
                    wv.setSurfaceCopyPagesPanelHeight((this._copyPagesPanelHeightInPx - (deltaPx - availableSpace)) / onePercentToPx);
                }
                else
                {
                    // possibly will be broken layout, need to collapse some opened sections
                    wv.setSurfaceCopyPagesPanelHeight(this._copyPagesMintHeightPx / onePercentToPx);
                }
            }
        }
        else
        {
            // all 3 are collapsed
            wv.setSurfaceCopyPagesPanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - 3 * SECTION_COLLAPSED_HEIGHT_PX) / onePercentToPx
            );
        }
    }

    @action.bound
    private _onCopyPagesPanelHide():void
    {
        const {workbenchVisibility: wv} = controllers.ui;
        const isSurfacePagesOutlinePanelCollapsed:boolean = wv.isSurfacePagesOutlinePanelCollapsed();
        const isSurfaceComponentsPanelCollapsed:boolean = wv.isSurfaceComponentsPanelCollapsed();
        const isSurfaceBoxesOutlinePanelCollapsed:boolean = wv.isSurfaceBoxesOutlinePanelCollapsed();
        const onePercentToPx:number = this._heightOfSurfaceOutlinePanelInPx / 100;

        wv.setSurfaceCopyPagesPanelHeight(0);

        if( !isSurfaceBoxesOutlinePanelCollapsed )
        {
            wv.setSurfaceBoxesOutlinePanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - this._componentsPanelHeightInPx - this._pagesPanelHeightInPx) / onePercentToPx
            );
        }
        else if( !isSurfaceComponentsPanelCollapsed )
        {
            wv.setSurfaceComponentsPanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - this._boxesPanelHeightInPx - this._pagesPanelHeightInPx) / onePercentToPx
            );
        }
        else if( !isSurfacePagesOutlinePanelCollapsed )
        {
            wv.setSurfacePagesOutlinePanelHeight(
                (this._heightOfSurfaceOutlinePanelInPx - this._boxesPanelHeightInPx - this._componentsPanelHeightInPx) / onePercentToPx
            );
        }
    }

    public render():JSX.Element
    {
        const {surfaceArea, workbenchVisibility: wv, surfacePagesPanel} = controllers.ui;
        const {copyFromResizerInProgressOffset, pagesResizerInProgressOffset, componentsResizerInProgressOffset} = this._state;
        const {className} = this.props;

        const isSurfaceCopyPagesPanelShown:boolean = wv.isSurfaceCopyPagesPanelShown();
        const isSurfacePagesOutlinePanelCollapsed:boolean = wv.isSurfacePagesOutlinePanelCollapsed();
        const isSurfaceComponentsPanelCollapsed:boolean = wv.isSurfaceComponentsPanelCollapsed();
        const isSurfaceBoxesOutlinePanelCollapsed:boolean = wv.isSurfaceBoxesOutlinePanelCollapsed();

        const isCopyPagesResizerInProgress:boolean = copyFromResizerInProgressOffset !== 0;
        const isPagesResizerInProgress:boolean = pagesResizerInProgressOffset !== 0;
        const isComponentsResizerInProgress:boolean = componentsResizerInProgressOffset !== 0;

        if( surfaceArea.isInPreviewOrZenPreviewMode() )
            return (
                <Section className={styles.previewMode}>
                    Preview mode
                </Section>
            );

        return (
            <div
                className={cx('base', {
                    removeHover: isCopyPagesResizerInProgress || isPagesResizerInProgress || isComponentsResizerInProgress,
                    [className]: !!className,
                })}
                ref={this._refHandlerSurfaceOutline}
            >
                {isSurfaceCopyPagesPanelShown && <div
                    className={cx('sectionWithResizer')}
                    style={{
                        height: this._copyPagesWrapperHeight,
                        zIndex: isCopyPagesResizerInProgress ? 5 : 'auto'
                    }}
                >
                    <SurfaceCopyPagesOutline/>

                    {isSurfaceCopyPagesPanelShown &&
                    !(isSurfacePagesOutlinePanelCollapsed && isSurfaceComponentsPanelCollapsed && isSurfaceBoxesOutlinePanelCollapsed)
                    && <Resizer
                        isVisible={true}
                        isVertical={false}
                        className={cx('surfaceOutlinePanelResizer')}
                        dragGhostDirection={DragGhostDirection.Bottom}
                        dragGhostOffset={this._getCopyFromMaxResizerInProgressOffset()}
                        onResize={this._handleCopyFromOutlineResize}
                    />}
                </div>
                }

                <div
                    className={cx('sectionWithResizer')}
                    style={{
                        height: this._pagesOutlineWrapperHeight,
                        zIndex: isPagesResizerInProgress ? 5 : 'auto',
                    }}
                >
                    <SurfacePagesOutline
                        ref={(ref) => surfacePagesPanel.setPagesComponentInstance(ref)}
                        onExpand={this._handlePagesOutlinePanelExpand}
                        onCollapse={this._handlePagesOutlinePanelCollapse}
                    />

                    {!isSurfacePagesOutlinePanelCollapsed &&
                    !(isSurfaceComponentsPanelCollapsed && isSurfaceBoxesOutlinePanelCollapsed)
                    && <Resizer
                        isVisible={true}
                        isVertical={false}
                        className={cx('surfaceOutlinePanelResizer')}
                        dragGhostDirection={DragGhostDirection.Bottom}
                        dragGhostOffset={this._getPagesOutlineMaxResizerInProgressOffset()}
                        onResize={this._handlePagesOutlineResize}
                    />}
                </div>

                <div
                    className={cx('sectionWithResizer')}
                    style={{
                        height: this._componentsWrapperHeight,
                        zIndex: isComponentsResizerInProgress ? 5 : 'auto',
                    }}
                >
                    <SurfaceComponents
                        onExpand={this._handleComponentsPanelExpand}
                        onCollapse={this._handleComponentsPanelCollapse}
                    />

                    {!isSurfaceComponentsPanelCollapsed && !isSurfaceBoxesOutlinePanelCollapsed && <Resizer
                        isVisible={true}
                        isVertical={false}
                        className={cx('surfaceOutlinePanelResizer')}
                        dragGhostDirection={DragGhostDirection.Bottom}
                        dragGhostOffset={this._getComponentsMaxResizerInProgressOffset()}
                        onResize={this._handleComponentsResize}
                    />}
                </div>

                <div
                    className={cx('boxesOutlineWrapper')}
                    style={{
                        height: this._boxesOutlineWrapperHeight,
                    }}
                >
                    <SurfaceBoxesOutline
                        onCollapse={this._handleBoxesOutlinePanelCollapse}
                        onExpand={this._handleBoxesOutlinePanelExpand}
                    />
                </div>
            </div>
        );
    }
}
